import { Stack } from "@bps/fluent-ui";
import { DateTimeWithDurationSinceTooltip } from "@components/DateTimeWithDurationSinceTooltip";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { FieldDeviceClaim } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useFieldDeviceClaims } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { ClaimsFilterValues } from "./ClaimsFilter";
import { ClaimsValueDisplay } from "./ClaimsValueDisplay";

interface ClaimsProps {
  deviceId: string;
  filter: ClaimsFilterValues;
}

export const ClaimsTable = ({ deviceId, filter }: ClaimsProps) => {
  const claimsQuery = useFieldDeviceClaims(deviceId);
  const { data, isLoading, error } = claimsQuery;
  const errorMessage = error?.message;

  const columns: DataTableColumn<FieldDeviceClaim>[] = [
    {
      key: "key",
      name: "Key",
      minWidth: 150,
      maxWidth: 300,
      isResizable: true,
      isSorted: true,
      sort: true,
      onRender: (claim: FieldDeviceClaim) => claim.key
    },
    {
      key: "value",
      name: "Value",
      minWidth: 150,
      isResizable: true,
      onRender: (claim: FieldDeviceClaim) => (
        <ClaimsValueDisplay claim={claim} />
      )
    },
    {
      key: "createdDate",
      name: "Created Date",
      minWidth: 150,
      onRender: (claim: FieldDeviceClaim) => (
        <DateTimeWithDurationSinceTooltip
          dateUtc={claim.changeLog?.createdDate}
        />
      )
    },
    {
      key: "createdBy",
      name: "Created By",
      minWidth: 150,
      onRender: (claim: FieldDeviceClaim) => claim.changeLog?.createdBy
    },
    {
      key: "updatedDate",
      name: "Last Updated Date",
      minWidth: 150,
      onRender: (claim: FieldDeviceClaim) => (
        <DateTimeWithDurationSinceTooltip
          dateUtc={claim.changeLog?.updatedDate}
        />
      )
    },
    {
      key: "updatedBy",
      name: "Last Updated By",
      minWidth: 150,
      onRender: (claim: FieldDeviceClaim) => claim.changeLog?.updatedBy
    }
  ];

  return (
    <Stack
      verticalFill
      styles={{
        root: {
          overflowY: "auto"
        }
      }}
    >
      <DataTable
        stickyHeader
        shimmerLines={10}
        columns={columns}
        onShouldVirtualize={() => true}
        items={filterAndSortClaims(data, filter)}
        enableShimmer={isLoading}
        errorMessage={errorMessage}
      />
    </Stack>
  );
};

const filterAndSortClaims = (
  claims: FieldDeviceClaim[] | undefined,
  filter: ClaimsFilterValues
): FieldDeviceClaim[] => {
  if (!claims) return [];

  return claims
    .filter(
      d =>
        filter.name === "" ||
        d.key.toLowerCase().indexOf(filter.name.toLowerCase()) >= 0 ||
        (d.value &&
          d.value.toLowerCase().indexOf(filter.name.toLowerCase()) >= 0)
    )
    .sort((a: FieldDeviceClaim, b: FieldDeviceClaim) => {
      return a.key < b.key ? -1 : 1;
    });
};
